/**
 * Annotative shell sub-reducer — emits audit events for the 9 annotative
 * commands added by PR-1..PR-16 (set-cannoscale-and-sync, ANNOUPDATE,
 * add/remove-annotation-context, three header toggles, scalelist
 * add/remove). Before PR-17 these commands fell through to `default →
 * return state` in `reduceEditorShellState`, so the command-line shell
 * never confirmed the action and the audit event log never reflected
 * it. This sub-reducer pushes a localised audit event matching what
 * AutoCAD echoes for the same commands.
 *
 * Mirrors the structure of `shellUiReducer.ts` — pure function, returns
 * `null` when the command isn't handled here (so the dispatcher falls
 * through to the next sub-reducer).
 *
 * NOTE: For the three toggle commands the audit summary needs the NEW
 * (post-flip) header value — but the shell reducer runs BEFORE the
 * geometry document mutates. Solution: caller passes the current
 * `DrawingDocument` snapshot as `geometryDocument`; we predict the
 * post-flip value by mirroring the same flip that
 * `geometryReducerAnnotative.ts` applies (boolean inversion for
 * annoAllVisible/annoMonitor, sign-flip for the int annoAutoScale).
 *
 * Passing the document explicitly keeps this module a pure function and
 * avoids a circular import on `useEditorStore` from editorStore.ts (the
 * same constraint that `reducerHelpers.ts` documents at file head).
 */

import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
} from "@dxf-ai-editor/contracts";
import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";
import { pushAuditEvent } from "./reducerHelpers";

/**
 * Best-effort extraction of a `scaleName` from the `unknown` annotation
 * context payload carried by `entity/add-annotation-context`. The
 * context is an `AnnotScaleContext` (discriminated union over per-entity
 * variants) but the contract layer types it as `unknown` to avoid
 * coupling with `@dxf-ai-editor/drawing-model`. All variants share the
 * `scaleName` base field (annotative.ts:96-101), so a structural pluck
 * is safe at runtime.
 */
function extractScaleNameFromContext(context: unknown): string {
  if (
    context !== null &&
    typeof context === "object" &&
    "scaleName" in context
  ) {
    const value = (context as { scaleName?: unknown }).scaleName;
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return "?";
}

/**
 * Maps a header boolean / int value to the localised "вкл" / "выкл"
 * (or "on" / "off") suffix used by the toggle audit summaries.
 *
 * For `annoAutoScale` AutoCAD treats positive values (1..4) as enabled
 * and zero/negative values as disabled — see plan §4 ANNOAUTOSCALE
 * sign-flip semantics. We mirror that here.
 */
function statusLabel(enabled: boolean): string {
  return enabled ? t("status.on") : t("status.off");
}

export function reduceAnnotativeShellCommand(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  geometryDocument: DrawingDocument,
): EditorShellState | null {
  switch (command.type) {
    case "header/set-cannoscale-and-sync": {
      // CANNOSCALE switch — AutoCAD echoes the new scale name on the
      // command-line. The materialization pass (when ANNOAUTOSCALE > 0)
      // happens inside the geometry reducer; the shell only logs.
      return pushAuditEvent(
        state,
        command,
        t("audit.cannoscale_changed", { scale: command.cannoscaleName }),
        "success",
      );
    }

    case "geometry/annoupdate": {
      // ANNOUPDATE / ANNORESET — both AutoCAD descriptors funnel through
      // this command literal (see command-registry/system.ts). The
      // summary doesn't carry a {count} placeholder because the shell
      // reducer fires before the geometry mutation; emitting an
      // accurate count would require post-mutation accounting outside
      // the pure-function contract of sub-reducers.
      return pushAuditEvent(
        state,
        command,
        t("audit.annoupdate_done"),
        "success",
      );
    }

    case "entity/add-annotation-context": {
      const scaleName = extractScaleNameFromContext(command.context);
      return pushAuditEvent(
        state,
        command,
        t("audit.context_added", {
          scale: scaleName,
          entityId: command.entityId,
        }),
        "success",
      );
    }

    case "entity/remove-annotation-context": {
      return pushAuditEvent(
        state,
        command,
        t("audit.context_removed", {
          scale: command.scaleName,
          entityId: command.entityId,
        }),
        "success",
      );
    }

    case "header/toggle-annoallvisible": {
      // Predict the post-flip value (boolean inversion — see
      // geometryReducerAnnotative.ts:451).
      const oldValue = geometryDocument.annoAllVisible ?? true;
      const newValue = !oldValue;
      return pushAuditEvent(
        state,
        command,
        t("audit.annoallvisible_toggled", { state: statusLabel(newValue) }),
        "info",
      );
    }

    case "header/toggle-annoautoscale": {
      // Sign-flip semantics (geometryReducerAnnotative.ts:458): if cur
      // > 0 the toggle drives it negative; if cur <= 0 it resets to 4.
      // "Enabled" maps to positive values (default 4); "disabled" to
      // zero or negative.
      const cur = geometryDocument.annoAutoScale ?? 4;
      const newValue = cur > 0 ? -Math.abs(cur) : 4;
      return pushAuditEvent(
        state,
        command,
        t("audit.annoautoscale_toggled", {
          state: statusLabel(newValue > 0),
        }),
        "info",
      );
    }

    case "header/toggle-annomonitor": {
      const oldValue = geometryDocument.annoMonitor ?? false;
      const newValue = !oldValue;
      return pushAuditEvent(
        state,
        command,
        t("audit.annomonitor_toggled", { state: statusLabel(newValue) }),
        "info",
      );
    }

    case "header/scalelist-add": {
      return pushAuditEvent(
        state,
        command,
        t("audit.scalelist_added", {
          name: command.scaleName,
          factor: command.scaleFactor,
        }),
        "success",
      );
    }

    case "header/scalelist-remove": {
      return pushAuditEvent(
        state,
        command,
        t("audit.scalelist_removed", { name: command.scaleName }),
        "success",
      );
    }

    default:
      return null;
  }
}
